import { AppConstants } from 'src/constants/constants';
import { HttpService } from './http.service';
import { Injectable } from '@angular/core';
import { UtilService } from 'src/app/services/util.service';
import { fhirR4 } from '@smile-cdr/fhirts';

export interface SiteServiceCode {
  siteNumber: number;
  serviceCode: string;
  effectiveStartDate: Date;
  effectiveEndDate: Date;
  active: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class SiteServiceCodeService {

  private siteServiceCodeList: SiteServiceCode[];

constructor(
  private httpService: HttpService,
  private utilService: UtilService
  ) {

  }

  getSiteAvailableEntryCategory(siteNumber: number, isSiteActive: boolean): string[] {
    return this.getAvailableParts(siteNumber, isSiteActive, 0);
  }

  getSiteAvailableServiceArea(siteNumber: number, isSiteActive: boolean): string[] {
    return this.getAvailableParts(siteNumber, isSiteActive, 1);
  }


  getSiteAvailableServiceDetail1(siteNumber: number, isSiteActive: boolean, serviceAreaValue: string): string[] {
    return this.getAvailableParts(siteNumber, isSiteActive, 2, serviceAreaValue);
  }

  getSiteAvailableServiceDetail2(siteNumber: number, isSiteActive: boolean, serviceDetail1Value: string): string[] {
    return this.getAvailableParts(siteNumber, isSiteActive, 3, serviceDetail1Value);
  }

  isValidEntryCategoryValue(siteNumber: number, entryCategory: string): boolean {
    if (!entryCategory || !siteNumber) {
      return true;
    }
    const allItems = this.getAvailableParts(siteNumber, false, 0);
    return allItems.find(x => x === entryCategory)?.length > 0;
  }

  isValidServiceAreaValue(siteNumber: number, serviceAreaValue: string): boolean {
    if (!serviceAreaValue || !siteNumber) {
      return true;
    }
    const allItems = this.getAvailableParts(siteNumber, false, 1);
    return allItems.find(x => x === serviceAreaValue)?.length > 0;
  }

  isValidServiceDetail1Value(siteNumber: number, serviceAreaValue: string, serviceDetail1Value: string): boolean {
    if (!serviceDetail1Value || !siteNumber) {
      return true;
    }
    const allItems = this.getAvailableParts(siteNumber, false, 2, serviceAreaValue);
    const matched = allItems.find(x => x === serviceDetail1Value);
    return (matched?.length > 0);
  }

  isValidServiceDetail2Value(siteNumber: number,
                             serviceDetail1Value: string, serviceDetail2Value: string): boolean {
    if (!serviceDetail2Value || !siteNumber) {
      return true;
    }
    const allItems = this.getAvailableParts(siteNumber, false, 3, serviceDetail1Value);
    return allItems.find(x => x === serviceDetail2Value)?.length > 0;
  }

  async getSitesPerformService(): Promise<SiteServiceCode[]> {
    await this.loadSiteServiceCodeList();
    return this.siteServiceCodeList;
  }

  private getAvailableParts(siteNumber: number, isSiteActive: boolean, partIndex: number, parentValue?: string): string[] {
    const rtList = [];

    if (!this.siteServiceCodeList) {
      return rtList;
    }
    const filtered = this.siteServiceCodeList?.filter(s => s.siteNumber === siteNumber);
    filtered?.forEach( s => {
      // #2646 use active flag instead of effective date range
      let ignore = false;
      if (isSiteActive && !s.active) {
        ignore = true;
      }
      if (!ignore) {
        const splitterArray = s.serviceCode.split('.');
        if (parentValue) {
          if (splitterArray[partIndex - 1] === parentValue) {
            rtList.push(splitterArray[partIndex]);
          }
        } else {
          rtList.push(splitterArray[partIndex]);
        }
      }
    });
    return [...new Set(rtList)];
  }

  async isSiteInActiveList(siteNumber: string): Promise<boolean> {
    if (!this.siteServiceCodeList || this.siteServiceCodeList?.length === 0) {
      await this.loadSiteServiceCodeList();
    }
    const sites = this.siteServiceCodeList?.filter (
      x =>  x.siteNumber === +siteNumber && x.active);
    return sites?.length > 0;
  }

  async isSiteInList(siteNumber: string): Promise<boolean> {
    if (!this.siteServiceCodeList || this.siteServiceCodeList?.length === 0) {
      await this.loadSiteServiceCodeList();
    }
    const sites = this.siteServiceCodeList?.filter (
      x =>  x.siteNumber === +siteNumber);
    return sites?.length > 0;
  }

  loadSiteServiceCodeList(): Promise<void> {
    if (this.siteServiceCodeList?.length > 0) {
      return;
    }
    return this.httpService.getSiteServiceCodes()
    .then((data: fhirR4.Bundle) => {
      if (data.entry?.length > 0) {
        this.siteServiceCodeList = [];
        data.entry.forEach( entry => {
          const siteService = entry.resource as fhirR4.HealthcareService;
          const oneItem: SiteServiceCode = {
            siteNumber : +this.findSiteNumberFromEntry(siteService),
            active : siteService.active,
            // tslint:disable-next-line: max-line-length
            serviceCode: this.utilService.getCodeFromCodeableConceptStd(siteService.type, AppConstants.serviceTypeSiteServiceSystem, 'code'),
            effectiveStartDate: siteService.extension?.find (x => x.url === AppConstants.serviceExtensionPeriodSystem).valuePeriod.start,
            effectiveEndDate: siteService.extension?.find (x => x.url === AppConstants.serviceExtensionPeriodSystem).valuePeriod.end
          };
          this.siteServiceCodeList.push(oneItem);
        });
      }
    });
  }

  findSiteNumberFromEntry(entry: fhirR4.HealthcareService): string {
    return entry.providedBy?.identifier?.value;
  }
}
